import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  FiBookOpen,
  FiDownload,
  FiFileText,
  FiAward,
  FiSearch,
  FiExternalLink,
} from 'react-icons/fi';
import { BRAND, GOLD_GRADIENT, Page, PageHeader, Card, Tag, Button, Kicker, Alert, EmptyState, inputStyle, fadeUp } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/notebook.jpg', pos: 'center 30%', height: 640, opacity: 0.6 };

// ---------------------------------------------------------------------------
// Resource catalog (statically defined: PDFs live in /public/resources/)
// ---------------------------------------------------------------------------
const MASTER_MANUAL = {
  title: 'CORE for Schools — Instructor Manual',
  subtitle: 'The complete companion to the 18 interactive lessons — written for teachers who are not cybersecurity experts',
  description:
    'Printable A4 manual aligned with the current decks. For every lesson: the story ' +
    'and the cast, a pre-class self-study panel (study time, prerequisites, self-check), ' +
    'a 5-minute quick start, objectives and the lesson\'s own vocabulary, a chapter-by-chapter ' +
    'guide with the characters\' lines, analogies and pitfalls, the full answer key of every ' +
    'mission (knowledge checks, sorting missions, choose-the-move stories, settings checklists, ' +
    'labs) with the badges they award, off-script Q&A, discussion prompts, exit tickets and the ' +
    'family task. Plus: meet-the-cast page, master glossary, rubric, index of missions and sources.',
  filename: 'CORE7_Instructor_Manual.pdf',
  href: '/resources/CORE7_Instructor_Manual.pdf',
  meta: '~330 pages · A4 · ~3 MB',
};

const LESSONS = [
  { n: 1, title: 'An Overview of Cyber Security' },
  { n: 2, title: 'Defense Tools & Best Practices Online' },
  { n: 3, title: 'Actors and Criminals of the Digital World' },
  { n: 4, title: 'Malware & Social Engineering Attacks' },
  { n: 5, title: 'Network Attacks & Hardware and Infrastructure Attacks' },
  { n: 6, title: 'Application Attacks & Risks Associated with Cloud Computing' },
  { n: 7, title: 'Fake News and Digital Manipulation' },
  { n: 8, title: 'The Fundamentals of Cybersecurity' },
  { n: 9, title: 'Security Architecture' },
  { n: 10, title: 'Network Security' },
  { n: 11, title: 'Application Security' },
  { n: 12, title: 'The Impact of New Technologies (Artificial Intelligence)' },
  { n: 13, title: 'The Impact of New Technologies (Blockchain & Quantum Computing)' },
  { n: 14, title: 'What if... The Worst Case Scenario' },
  { n: 15, title: 'Ransomware & Real Cases' },
  { n: 16, title: 'Cyberbullying & Social Media Security' },
  { n: 17, title: 'Privacy Management & Digital Reputation' },
  { n: 18, title: 'Careers and Opportunities' },
].map((l) => ({
  ...l,
  href: `/resources/lessons/lesson-${String(l.n).padStart(2, '0')}-concepts.pdf`,
  filename: `lesson-${String(l.n).padStart(2, '0')}-concepts.pdf`,
}));

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AdminResources() {
  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();
  const filteredLessons = q
    ? LESSONS.filter(
        (l) =>
          l.title.toLowerCase().includes(q) ||
          String(l.n).includes(q),
      )
    : LESSONS;

  return (
    <Page hero={HERO}>
      <style>{`
        .core-res-table { min-width: 620px; }
        @media (max-width: 640px) {
          .core-res-hide-sm { display: none !important; }
          .core-res-actions { flex-direction: column; align-items: stretch !important; }
        }
      `}</style>

      <PageHeader
        kicker="Administration"
        title="Teacher resources"
        subtitle="Printable materials to prepare and run the lessons — admin only."
        right={
          <div style={{ position: 'relative', minWidth: 240 }}>
            <FiSearch size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: BRAND.muted, pointerEvents: 'none' }} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search lesson…"
              style={{ ...inputStyle(false), borderRadius: 999, padding: '10px 14px 10px 40px' }}
            />
          </div>
        }
      />

      {/* Featured: complete instructor manual */}
      <motion.section variants={fadeUp} initial="hidden" animate="visible"
        style={{ position: 'relative', borderRadius: 24, overflow: 'hidden', background: BRAND.navy800, color: BRAND.ivory, border: `1px solid ${BRAND.line}`, boxShadow: '0 20px 50px rgba(7,20,45,0.28)' }}>
        <img src={BRAND.bg.school} alt="" aria-hidden style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.18 }} />
        <div aria-hidden style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(7,20,45,0.97) 0%, rgba(7,20,45,0.88) 55%, rgba(7,20,45,0.6) 100%)' }} />
        <div className="core-res-actions" style={{ position: 'relative', zIndex: 1, padding: 'clamp(20px,3vw,30px)', display: 'flex', gap: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ width: 64, height: 64, borderRadius: 18, display: 'grid', placeItems: 'center', background: GOLD_GRADIENT, color: BRAND.navy900, flexShrink: 0, boxShadow: '0 10px 28px rgba(205,155,80,0.35)' }}>
            <FiAward size={30} />
          </span>
          <div style={{ flex: '1 1 320px', minWidth: 240 }}>
            <Kicker>Complete manual · Rubric</Kicker>
            <h2 style={{ fontSize: 'clamp(1.2rem,2.6vw,1.6rem)', fontWeight: 900, margin: '6px 0 4px', color: BRAND.ivory, lineHeight: 1.15 }}>{MASTER_MANUAL.title}</h2>
            <p style={{ fontFamily: BRAND.serif, fontStyle: 'italic', fontSize: '1.1rem', color: 'rgba(249,240,231,0.86)', margin: '0 0 8px' }}>{MASTER_MANUAL.subtitle}</p>
            <p style={{ fontSize: '0.9rem', lineHeight: 1.55, color: BRAND.muted, margin: '0 0 10px' }}>{MASTER_MANUAL.description}</p>
            <Tag tone="muted">{MASTER_MANUAL.meta}</Tag>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignSelf: 'center' }}>
            <Button variant="gold" href={MASTER_MANUAL.href} target="_blank" rel="noopener noreferrer">
              <FiExternalLink size={16} /> Open PDF
            </Button>
            <Button variant="outline" href={MASTER_MANUAL.href} download={MASTER_MANUAL.filename}>
              <FiDownload size={16} /> Download
            </Button>
          </div>
        </div>
      </motion.section>

      {/* Per-lesson guides */}
      <motion.section variants={fadeUp} initial="hidden" animate="visible" style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
          <div style={{ flex: '1 1 320px' }}>
            <Kicker>Per-lesson concept guides</Kicker>
            <h2 style={{ fontSize: 'clamp(1.25rem,2.8vw,1.55rem)', fontWeight: 900, color: BRAND.ivory, margin: '4px 0 6px' }}>18 PDFs — one per lesson</h2>
            <p style={{ color: BRAND.muted, fontSize: '0.92rem', lineHeight: 1.55, maxWidth: 760, margin: 0 }}>
              Each PDF is generated from the lesson deck itself, so it always matches what students see:
              the <strong style={{ color: BRAND.ivory }}>story and the cast</strong>, a pre-class self-study panel,
              a 5-minute cheat sheet, every concept <strong style={{ color: BRAND.ivory }}>slide by slide</strong> with the
              characters&apos; lines, the <strong style={{ color: BRAND.ivory }}>full answer key of every mission</strong> and
              the badges, an enriched glossary (30-second explanation, example, watch-out), questions students
              may ask, and the take-home family page. Designed for teachers without a technical background.
            </p>
          </div>
          <Tag tone="gold">{filteredLessons.length} of {LESSONS.length} shown</Tag>
        </div>

        {filteredLessons.length === 0 ? (
          <EmptyState icon="🔎" title={`No lesson matches “${query}”.`} text="Try a lesson number or a word from the title." action={<Button variant="outline" size="sm" onClick={() => setQuery('')}>Clear search</Button>} />
        ) : (
          <Card style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table className="core-table core-res-table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>#</th>
                    <th>Lesson</th>
                    <th className="core-res-hide-sm">Format</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLessons.map((lesson, i) => (
                    <motion.tr
                      key={lesson.n}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.03 * i, duration: 0.25 }}
                    >
                      <td>
                        <span style={{ fontSize: '1.4rem', fontWeight: 900, color: BRAND.goldLight, lineHeight: 1 }}>{String(lesson.n).padStart(2, '0')}</span>
                      </td>
                      <td>
                        <div style={{ fontWeight: 800, color: BRAND.ivory, lineHeight: 1.3 }}>{lesson.title}</div>
                        <div style={{ fontSize: '0.74rem', color: BRAND.muted, fontWeight: 600, marginTop: 2 }}>{lesson.filename}</div>
                      </td>
                      <td className="core-res-hide-sm">
                        <Tag tone="muted"><FiFileText size={11} /> PDF</Tag>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <Button variant="ghost" size="sm" href={lesson.href} target="_blank" rel="noopener noreferrer" title="Open in browser">
                            <FiExternalLink size={14} /> Open
                          </Button>
                          <Button variant="gold" size="sm" href={lesson.href} download={lesson.filename} title="Download PDF">
                            <FiDownload size={14} /> Download
                          </Button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </motion.section>

      {/* Footer note */}
      <Alert tone="gold" style={{ marginTop: 28, display: 'flex', gap: 12, alignItems: 'flex-start', lineHeight: 1.5 }}>
        <FiBookOpen size={18} style={{ color: BRAND.goldLight, marginTop: 2, flexShrink: 0 }} />
        <span>
          <strong>Tip:</strong> print the PDF for the lesson you are about to
          teach and bring it to class as a quick reference. Keep the complete
          manual as the overall programme rubric.
        </span>
      </Alert>
    </Page>
  );
}
